/// <amd-module name="Feed/Legacy/Components/Settings/AddCollectionInternalSourcePanelSection" />
import * as React from "react";

import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";
import { TextField } from "OfficeFabric/TextField";
import { findIndex } from "OfficeFabric/Utilities";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";

import { Feed, FeedView, FeedVisibility, UpstreamSource, UpstreamSourceType } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { AddUpstreamProtocolSelection } from "Feed/Legacy/Components/Settings/AddUpstreamProtocolSelection";
import { UpstreamConstants } from "Feed/Legacy/Components/Settings/CommonTypes";
import { IInternalUpstreamSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { isPublicFeed } from "Feed/Legacy/Helpers/ProjectHelper";
import { getPermissionSettingsUrl } from "Feed/Legacy/Helpers/UrlHelper";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IAddCollectionInternalSourcePanelSectionProps {
    pageContext: IVssPageContext;
    availableFeedsForUpstreams: Feed[];
    selectedUpstreamSourcesHandler: (sources: UpstreamSource[]) => void;
    currentInternalUpstreamSources: UpstreamSource[];
    upstreamSourceNameInvalidMessage?: string;
    internalUpstreamSettings: IInternalUpstreamSettingsState;
}

export interface IAddCollectionInternalSourcePanelSectionState {
    selectedFeed: Feed;
    viewsFromSelectedFeed: FeedView[];
    upstreamName: string;
    selectedView: FeedView;
    selectedProtocols: string[];
    getMessageBar: () => JSX.Element;
}

export class AddCollectionInternalSourcePanelSection extends React.Component<
    IAddCollectionInternalSourcePanelSectionProps,
    IAddCollectionInternalSourcePanelSectionState
> {
    private _isUpstreamNameDirty: boolean = false;
    private _feedsDataService: IFeedDataService;
    private _collectionId: string;
    private currentInternalUpstreamsMap: { [feedId: string]: string[] };
    private _dropdown: Dropdown = null;
    private _isUpackSameCollectionUpstreamAvailable: boolean;

    constructor(props: IAddCollectionInternalSourcePanelSectionProps) {
        super(props);
        const pageData: IPageData = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData();
        this._collectionId = pageData.hostId;
        /*
        NOTE: Do not repeat this pattern until it has been approved
         */
        this._feedsDataService = this.props.pageContext.getService<IFeedDataService>("IFeedDataService");

        this._isUpackSameCollectionUpstreamAvailable = isFeatureFlagEnabled(this.props.pageContext,
            "Packaging.Feed.UPackSameCollectionUpstreams", false);

        this.currentInternalUpstreamsMap = {};
        props.currentInternalUpstreamSources.forEach((source: UpstreamSource) => {
            if (source.upstreamSourceType !== UpstreamSourceType.Internal) {
                return;
            }

            if (source.internalUpstreamFeedId == null) {
                return;
            }

            if (!this.currentInternalUpstreamsMap[source.internalUpstreamFeedId]) {
                this.currentInternalUpstreamsMap[source.internalUpstreamFeedId] = [] as string[];
            }

            this.currentInternalUpstreamsMap[source.internalUpstreamFeedId].push(source.internalUpstreamViewId);
        });

        this.state = {
            selectedFeed: null,
            viewsFromSelectedFeed: null,
            upstreamName: "",
            selectedView: null,
            selectedProtocols: [],
            getMessageBar: () => null
        };
    }

    public render(): JSX.Element {
        return (
            <div>
                <div className="upstream-panel-element">
                    <Dropdown
                        placeHolder={PackageResources.AddUpstream_FeedSelectorPlaceholder}
                        label={PackageResources.AddUpstream_FeedSelectorLabel}
                        options={this.props.availableFeedsForUpstreams.map((feed: Feed) => ({
                            key: feed.id,
                            text: feed.name,
                            data: feed
                        }))}
                        onChanged={this._onSelectedFeedChanged}
                        required={true}
                        selectedKey={!!this.state.selectedFeed ? this.state.selectedFeed.id : null}
                        ref={(element: Dropdown) => (this._dropdown = element)}
                    />
                </div>
                {this.state.getMessageBar()}
                <div className="upstream-panel-element">
                    <AddUpstreamProtocolSelection
                        selectedProtocolsChangedHandler={this.selectedProtocolsChangedHandler}
                        internalUpstreamSettings={this.props.internalUpstreamSettings}
                        isUPackSameCollectionUpstreamAvailable={this._isUpackSameCollectionUpstreamAvailable}
                        isUPackCrossCollectionUpstreamAvailable={false}
                    />
                </div>
                <div className="upstream-panel-element">
                    <Dropdown
                        placeHolder={this._getViewSelectorPlaceholderText()}
                        label={PackageResources.AddUpstream_ViewSelectorLabel}
                        options={this._getViewOptions()}
                        disabled={!this.state.viewsFromSelectedFeed || this.state.viewsFromSelectedFeed.length === 0}
                        onChanged={this._onSelectedViewChanged}
                        required={true}
                        selectedKey={!!this.state.selectedView ? this.state.selectedView.id : null}
                    />
                </div>
                <div className="upstream-panel-element">
                    <TextField
                        label={PackageResources.AddUpstream_UpstreamNameTextFieldLabel}
                        value={this._getUpstreamName()}
                        required={true}
                        onChanged={this._onUpstreamNameChanged}
                        disabled={!this.state.selectedView}
                        maxLength={UpstreamConstants.MaxUpstreamNameLength}
                        errorMessage={this.props.upstreamSourceNameInvalidMessage}
                    />
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        this.setFocus();
    }

    private getNoUpstreamableViewsMessage(feedName: string): JSX.Element {
        const feedPermissionSettingsUrl = getPermissionSettingsUrl(this.props.pageContext, feedName);
        // TODO: link to shared view docs
        return (
            <MessageBar severity={MessageBarSeverity.Warning}>
                <FormatComponent format={PackageResources.AddUpstream_NoAvailableViewsMessage}>
                    <ExternalLink pageContext={this.props.pageContext} href={feedPermissionSettingsUrl}>
                        {PackageResources.AddUpstream_NoAvailableViewsMessage_FeedOwner}
                    </ExternalLink>
                    {PackageResources.AddUpstream_NoAvailableViewsMessage_SharedView}
                </FormatComponent>
            </MessageBar>
        );
    }

    private getNoMoreUpstreamableViewsMessage(): JSX.Element {
        return (
            <MessageBar severity={MessageBarSeverity.Warning}>
                {PackageResources.AddUpstream_NoMoreUpstreamableViewsMessage}
            </MessageBar>
        );
    }

    private _getViewOptions(): IDropdownOption[] {
        if (this.state.viewsFromSelectedFeed && this.state.viewsFromSelectedFeed.length > 0) {
            return this.state.viewsFromSelectedFeed.map((view: FeedView) => ({
                key: view.id,
                text: view.name,
                data: view,
                disabled: !isPublicFeed(this.state.selectedFeed) && view.visibility === FeedVisibility.Private
            }));
        }

        return [];
    }

    private _getViewSelectorPlaceholderText = (): string => {
        if (!this.state.viewsFromSelectedFeed) {
            return PackageResources.AddUpstream_ViewSelectorPlaceholder_SelectFeed;
        }

        if (this.state.viewsFromSelectedFeed.length === 0) {
            return PackageResources.AddUpstream_ViewSelectorPlaceholder_NoViewsAvailable;
        }

        return PackageResources.AddUpstream_ViewSelectorPlaceholder_Default;
    };

    private _onSelectedFeedChanged = (item: IDropdownOption): void => {
        if (item.data == null) {
            return;
        }

        // Clear the upstream source in the parent
        this.props.selectedUpstreamSourcesHandler(null);

        const selectedFeed = item.data as Feed;
        const projectId = selectedFeed.project ? selectedFeed.project.id : null;
        /*
        NOTE: Do not repeat this pattern until it has been approved
         */
        this._feedsDataService.getFeedViewsAsync(projectId, selectedFeed.id).then((views: FeedView[]) => {
            const selectedViewsForFeed = this.currentInternalUpstreamsMap[selectedFeed.id] || ([] as string[]);

            const remainingViews: FeedView[] = views.filter(
                (view: FeedView) => selectedViewsForFeed.indexOf(view.id) < 0
            );

            const selectableViews: FeedView[] = remainingViews.filter(
                (view: FeedView) => isPublicFeed(selectedFeed) || view.visibility !== FeedVisibility.Private
            );

            const defaultViewIndex = findIndex(selectableViews, view => view && view.id === selectedFeed.defaultViewId);

            const selectedView =
                defaultViewIndex !== -1
                    ? selectableViews[defaultViewIndex]
                    : selectableViews.length > 0
                        ? selectableViews[0]
                        : null;

            let getMessageBar: () => JSX.Element = () => null;

            if (selectableViews.length === 0) {
                // In the past, user added a view from this feed
                // and at the moment, feed doesn't contain any more upstream-able views
                if (selectedViewsForFeed.length > 0) {
                    getMessageBar = () => this.getNoMoreUpstreamableViewsMessage();
                }

                // no views were upstreamed from this feed yet
                // and at the moment, feed doesn't contain any upstream-able views
                if (selectedViewsForFeed.length === 0) {
                    getMessageBar = () => this.getNoUpstreamableViewsMessage(selectedFeed.name);
                }
            }

            this.setState(
                {
                    viewsFromSelectedFeed: remainingViews,
                    selectedFeed,
                    upstreamName: this._getFormattedUpstreamName(selectedFeed, selectedView),
                    selectedView,
                    getMessageBar
                },
                () => this.props.selectedUpstreamSourcesHandler(this._getUpstreamSources())
            );
        });
    };

    private _onUpstreamNameChanged = (newValue: string): void => {
        FeedSettingsActionCreator.changeUpstreamSourceName.invoke(newValue);

        this._isUpstreamNameDirty = true;

        this.setState({ upstreamName: newValue }, () => {
            this.props.selectedUpstreamSourcesHandler(this._getUpstreamSources());
        });
    };

    private _onSelectedViewChanged = (item: IDropdownOption): void => {
        if (item.data == null) {
            return;
        }

        const selectedView = item.data as FeedView;
        this.setState(
            (prevState, props) => {
                return {
                    selectedView,
                    upstreamName: this._getFormattedUpstreamName(prevState.selectedFeed, selectedView)
                };
            },
            () => {
                this.props.selectedUpstreamSourcesHandler(this._getUpstreamSources());
            }
        );
    };

    private _getUpstreamName(): string {
        if (this._isUpstreamNameDirty) {
            return this.state.upstreamName;
        }

        return this._getFormattedUpstreamName(this.state.selectedFeed, this.state.selectedView);
    }

    private _getFormattedUpstreamName(feed: Feed, view: FeedView): string {
        if (!!feed && !!view) {
            let feedName = feed.name;
            let viewName = view.name;

            const currentLength = feedName.length + viewName.length;
            const maxLength = UpstreamConstants.MaxUpstreamNameLength - 1;

            if (currentLength > maxLength) {
                const sectionLength = maxLength / 2;
                feedName = feedName.substring(0, sectionLength);
                viewName = viewName.substring(0, sectionLength);
            }

            return `${feedName}@${viewName}`;
        }

        return "";
    }

    private selectedProtocolsChangedHandler = (protocols: string[]): void => {
        this.setState(
            {
                selectedProtocols: protocols
            },
            () => {
                this.props.selectedUpstreamSourcesHandler(this._getUpstreamSources());
            }
        );
    };

    private _getUpstreamSources(): UpstreamSource[] {
        if (
            this.state.selectedFeed != null &&
            this.state.selectedView != null &&
            this.state.upstreamName != null &&
            this.state.upstreamName.length > 0
        ) {
            const upstreamSources: UpstreamSource[] = [];

            for (const protocol of this.state.selectedProtocols) {
                const projectId = this.state.selectedFeed.project ? this.state.selectedFeed.project.id : null;
                upstreamSources.push({
                    id: null,
                    name: this.state.upstreamName,
                    location: null,
                    protocol,
                    upstreamSourceType: UpstreamSourceType.Internal,
                    deletedDate: null,
                    internalUpstreamCollectionId: this._collectionId,
                    internalUpstreamFeedId: this.state.selectedFeed.id,
                    internalUpstreamViewId: this.state.selectedView.id,
                    internalUpstreamProjectId: projectId
                } as UpstreamSource);
            }

            return upstreamSources.length > 0 ? upstreamSources : null;
        }

        return null;
    }

    private setFocus(): void {
        if (this._dropdown) {
            this._dropdown.focus();
        }
    }
}
